import {
    createFileRoute,
    redirect,
    useNavigate,
    useSearch,
} from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'

import type { MeQueryData } from '@/hooks/use-auth'
import { AUTH_ME_QUERY_KEY, useAuth } from '@/hooks/use-auth'
import { APP_NAME } from '@/lib/constants'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { queryClient } from '@/router'

const safeRedirect = (url?: string) => {
    if (!url || !url.startsWith('/') || url.startsWith('//')) return '/'
    return url
}

export const Route = createFileRoute('/login')({
    validateSearch: (search: Record<string, unknown>) => ({
        redirect: (search.redirect as string) || undefined,
    }),
    beforeLoad: ({ search }) => {
        const data = queryClient.getQueryData<MeQueryData>(AUTH_ME_QUERY_KEY)
        if (data?.user) {
            throw redirect({ to: safeRedirect(search.redirect) })
        }
    },
    component: LoginPage,
})

function LoginPage() {
    const { login } = useAuth()
    const navigate = useNavigate()
    const { redirect: redirectTo } = useSearch({ from: '/login' })
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsLoading(true)
        try {
            await login({ username, password })
            posthog.capture('user_logged_in', { username })
            void navigate({ to: safeRedirect(redirectTo) })
        } catch (err) {
            setError(err instanceof Error ? err.message : m.auth_login_failed())
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted">
            <div className="w-full max-w-md p-8 bg-card rounded-lg shadow">
                <h1 className="text-2xl font-bold text-center mb-6 text-primary">
                    {APP_NAME}
                </h1>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label
                            htmlFor="username"
                            className="block text-sm font-medium text-foreground"
                        >
                            {m.auth_username()}
                        </label>
                        <input
                            id="username"
                            type="text"
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-foreground"
                        >
                            {m.auth_password()}
                        </label>
                        <div className="relative mt-1">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={
                                    showPassword
                                        ? m.auth_hide_password()
                                        : m.auth_show_password()
                                }
                                aria-pressed={showPassword}
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded-r-md"
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                ) : (
                                    <Eye className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isLoading ? m.auth_logging_in() : m.auth_login()}
                    </button>
                </form>
            </div>
        </div>
    )
}
